import { useTranslation } from 'react-i18next';
import { i18nDynamic } from 'shared/i18n';
import { useState, useMemo, useCallback, type ChangeEvent } from 'react';
import { Spinner, Modal, Dropdown, DataImage } from 'app/components';
import { Clickable } from 'shared/ui/Clickable';
import { useImage, useBatchImages } from 'entities/document';
import { useStyle, useSaveStyle } from 'entities/style';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { useMovePageToSection, useGetSectionPages, useClonePage } from 'entities/document';
import { api } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { isRevisionMismatch } from 'shared/api/revision-mismatch';
import { Transforms, Editor, Element, type Descendant } from 'slate';
import { useSlate } from 'slate-react';
import { HistoryEditor } from 'slate-history';
import Tippy from '@tippyjs/react';
import { cloneDeepPage } from 'slate/components/page/utils';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { usePermissions } from 'app/api/permission-queries';
import { useDocumentTemplates } from 'entities/template';
import { customClassExtractor } from 'shared/lib/css';
import { DropdownList } from 'react-widgets';
import { useUser } from 'app/state/contexts/UserContext';
import { useEditorCss, useEditorDocument } from 'app/api/documents/useEditorData';
import { useSetEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCoarse, useEditorLiveSection } from 'app/state/contexts/EditorUiContext';
import type { DesignStyle, EditorDocument, SectionEntry } from 'entities/document';
import type { PageElement, CustomEditor } from 'slate/types/slate';
import type { DropdownItem } from 'shared/forms/Dropdown';

type PageStyleMap = Record<string, string>;
type StyleChange = Record<string, string | undefined>;

interface FormattedPage {
  sectionId: string;
  title: string;
  pageId: string | null;
  sectionName?: string;
  pageIndex: number;
  value: string;
  superId: string;
}

interface MovePayload {
  fromPage: FormattedPage;
  targetPage: FormattedPage;
  copyPage: boolean;
}

interface ImageFile {
  superId?: string;
  revisionId?: string;
  filename?: string;
  fileType: string;
  fileContent: string;
}

interface ImageData {
  file: ImageFile;
}

interface TemplatePageStructure {
  areas?: {
    header?: { hidden?: boolean };
    footer?: { hidden?: boolean };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface ClassOption {
  id: string;
  value: string;
  label: string;
}

interface HeaderFooterToggleProps {
  headerHidden?: boolean;
  footerHidden?: boolean;
  templateHeaderHidden?: boolean;
  templateFooterHidden?: boolean;
  onChange: (value: StyleChange) => void;
}

const HeaderFooterToggle = ({ headerHidden, footerHidden, templateHeaderHidden, templateFooterHidden, onChange }: HeaderFooterToggleProps) => {
  // Page-variable overrides template default. If no page-variable, fall back to template.

  const { t } = useTranslation('doc-editor');

  const effectiveHeaderHidden = headerHidden !== undefined ? headerHidden : !!templateHeaderHidden;
  const effectiveFooterHidden = footerHidden !== undefined ? footerHidden : !!templateFooterHidden;

  const handleHeaderToggle = () => {
    onChange({ xrpShowHeader: effectiveHeaderHidden ? undefined : 'none' });
  };

  const handleFooterToggle = () => {
    onChange({ xrpShowFooter: effectiveFooterHidden ? undefined : 'none' });
  };

  return (
    <div className="mb-3">
      <div className="text-blue-700 fw-bolder mb-2">{t('sidebar.page-settings.xrpShowHeaderFooter')}</div>
      <div className="d-flex flex-row gap-2">
        <label className="d-flex align-items-center gap-2 m-0 cursor-pointer">
          <input type="checkbox" className="form-check-input m-0" checked={!effectiveHeaderHidden} onChange={handleHeaderToggle} />
          <span>{t('sidebar.page-settings.xrpShowHeader')}</span>
        </label>
        <label className="d-flex align-items-center gap-2 m-0 cursor-pointer">
          <input type="checkbox" className="form-check-input m-0" checked={!effectiveFooterHidden} onChange={handleFooterToggle} />
          <span>{t('sidebar.page-settings.xrpShowFooter')}</span>
        </label>
      </div>
    </div>
  );
};

interface BackgroundPickerProps {
  name: string;
  onChange: (value: StyleChange) => void;
  defaultValue?: string | null;
}

const BackgroundPicker = ({ name, onChange, defaultValue }: BackgroundPickerProps) => {
  const { t } = useTranslation('doc-editor');

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<string | null | undefined>(defaultValue);
  const [imageError, setImageError] = useState(false);
  const handleImageError = useCallback(() => setImageError(true), []);
  const handleClose = () => {
    setIsOpen(false);
  };
  const { data: pDocument } = useEditorDocument();
  const content = pDocument?.content;
  const imageSuperId = defaultValue
    ?.match(/\((.*)\)/)
    ?.pop()
    ?.split('--xrp-resource-')[1];
  const imageRevisionId = content.images.filter((image) => image.superId === imageSuperId)[0]?.revisionId;
  const { data: fetchedImage } = useImage(imageRevisionId, 'thumbnail');

  const handleSelect = (image: ImageData) => {
    const newValue = `var(--xrp-resource-${image.file.superId})`;
    setValue(newValue);
    onChange({ [name]: newValue });
    setIsOpen(false);
  };

  const handleDelete = () => {
    setValue(undefined);
    onChange({ [name]: undefined });
  };

  const handleClick = () => {
    setIsOpen(true);
  };
  return (
    <div>
      <div className="text-blue-700 fw-bolder mb-1">{t('sidebar.page-settings.xrpBackroundImage')}</div>
      <div className="d-flex align-items-center">
        <Clickable className="border flex-grow-1 rounded d-flex align-items-center p-1" onClick={handleClick}>
          {imageError || !fetchedImage ? (
            <div className="bg-blue-300 aspect-1x1 me-2 d-flex align-items-center justify-content-center" style={{ width: '57px' }}>
              <i className="fa-regular fa-image fa-3x" />
            </div>
          ) : (
            <img
              alt=""
              src={`data:${fetchedImage.file?.fileType};base64,${fetchedImage.file?.fileContent}`}
              style={{ width: '57px', objectFit: 'cover' }}
              className="bg-blue-300 aspect-1x1 me-2"
              onError={handleImageError}
            />
          )}
          <div>{value || t('sidebar.page-settings.no-image')}</div>
        </Clickable>
        {value ? (
          <button type="button" className={`bg-transparent ms-2 border-0`} onClick={handleDelete}>
            <i className={`fa-regular fa-trash`} />
          </button>
        ) : null}
      </div>
      <BackgroundSelectorModal open={isOpen} onClose={handleClose} onSelect={handleSelect} />
    </div>
  );
};

interface SettingsSliderProps {
  name: string;
  onChange: (value: StyleChange) => void;
  defaultValue?: number | string;
  range: number[];
  step: number;
}

const SettingsSlider = ({ name, onChange, defaultValue, range, step }: SettingsSliderProps) => {
  const [value, setValue] = useState<number | string>(defaultValue ?? 0);
  const [saveSettings] = useDelayedSave(onChange, 200);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    saveSettings({ [name]: newValue });
  };

  return (
    <div className="mt-4">
      <div className="text-blue-700 fw-bolder">{i18nDynamic(`doc-editor:sidebar.page-settings.${name}`)}</div>
      <input
        type="range"
        min={range.at(0)}
        max={range.at(-1)}
        step={step}
        list={`markers-${name}`}
        className="w-100"
        onChange={handleChange}
        name={name}
        value={value}
      />
      <datalist id={`markers-${name}`}>
        {range.map((v) => (
          <option key={v} value={v}></option>
        ))}
      </datalist>
    </div>
  );
};

interface MovePageProps {
  selectedPage: number;
  activeSectionId: string;
  onMovePage: (value: MovePayload) => void;
  documentRevisionId: string;
  sections: SectionEntry[];
}

const MovePage = ({ selectedPage, activeSectionId, onMovePage, documentRevisionId, sections }: MovePageProps) => {
  const { t } = useTranslation('doc-editor');

  const sectionIds = sections.map((section) => section.revisionId);
  const { data: pDocument } = useEditorDocument();
  const [copyPage, setCopyPage] = useState(false);
  const { data, isLoading } = useGetSectionPages(documentRevisionId, sectionIds);

  const { sectionPermissions } = usePermissions(pDocument.superId);
  const formattedPages: FormattedPage[] =
    data?.flat().map((item) => {
      return {
        sectionId: item.sectionId,
        title: item.pageRevisionId ? `Sida ${item.idx}` : t('sidebar.page-settings.move-page.move-to-end'),
        pageId: item.pageRevisionId,
        sectionName: item.name,
        pageIndex: item.internalIdx,
        value: item.id,
        superId: item.superId,
      };
    }) ?? [];

  const fromPage = formattedPages.filter((page) => page.pageId !== null)[selectedPage - 1];
  const handleChange = (pageId: unknown) => {
    const targetPage = formattedPages.find((page) => page.value === pageId);

    onMovePage({ fromPage, targetPage, copyPage } as MovePayload);
  };
  return (
    <div className="mt-4 ">
      <div>
        <div className="flex flex-row items-center" style={{ marginBottom: '0.5rem' }}>
          <p className="text-blue-700 fw-bolder" style={{ marginBottom: 0 }}>
            {t('sidebar.page-settings.move-page.title')}
          </p>
          <Tippy content={<div className="bg-gray-100 p-2 rounded">{t('sidebar.page-settings.move-page.desc')}</div>}>
            <i className="fa-light fa-circle-question text-blue-700" style={{ display: 'flex', alignItems: 'center', marginLeft: '0.75rem' }}></i>
          </Tippy>
        </div>
        <div className="flex flex-row items-center mb-2">
          <p className="m-0 me-2">{t('sidebar.page-settings.move-page.copy')}</p>
          <input type="checkbox" role="switch" value={String(copyPage)} onChange={() => setCopyPage(!copyPage)} />
        </div>
      </div>
      {!isLoading && (
        <Dropdown
          sm
          items={formattedPages.filter((page) => (!copyPage ? page.sectionId !== activeSectionId : true))}
          value={''}
          onChange={handleChange}
          disabled={
            isLoading ||
            (!copyPage && sectionPermissions[fromPage?.superId] !== 'WRITE') ||
            formattedPages.filter((page) => (!copyPage ? page.sectionId !== activeSectionId : true) && sectionPermissions[page.superId] !== 'WRITE')
          }
          groupbyKey={'sectionName'}
          wrapper={({ item }: { item: DropdownItem }) => {
            return (
              <div className="d-flex align-items-center">
                <span>{item.title}</span>
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

interface BackgroundImageItemProps {
  imageData?: ImageData;
  onSelect: (imageData: ImageData) => void;
}

const BackgroundImageItem = ({ imageData, onSelect }: BackgroundImageItemProps) => {
  if (!imageData) {
    return (
      <div className="col-3 text-center">
        <div className="border bg-gray-100 rounded overflow-hidden d-flex align-items-center justify-content-center" style={{ height: '150px' }}>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="col-3 text-center">
      <Clickable onClick={() => onSelect(imageData)}>
        <div className="border bg-gray-100 rounded overflow-hidden" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <DataImage {...imageData.file} style={{ height: '100%' }} />
          <div className="border-top p-2">{imageData.file.filename}</div>
        </div>
      </Clickable>
    </div>
  );
};

interface BackgroundSelectorModalContentProps {
  onSelect: (imageData: ImageData) => void;
}

const BackgroundSelectorModalContent = ({ onSelect }: BackgroundSelectorModalContentProps) => {
  const { t } = useTranslation(['doc-editor', 'imageimport']);

  const { data: pDocument } = useEditorDocument();
  const revisionIds = useMemo(() => pDocument.content.images.map((image) => image.revisionId), [pDocument.content.images]);
  const { data: imageDataMap } = useBatchImages(pDocument.superId, revisionIds, 'thumbnail');

  return (
    <div>
      {pDocument.content.images.length === 0 && <h1 className="text-center">{t('imageimport:no-images')}</h1>}
      <div className="row">
        {pDocument.content.images.map((image) => (
          <BackgroundImageItem key={image.revisionId} imageData={imageDataMap[image.revisionId] as ImageData} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
};

interface BackgroundSelectorModalProps {
  onSelect: (imageData: ImageData) => void;
  open: boolean;
  onClose: () => void;
}

const BackgroundSelectorModal = ({ onSelect, open, onClose }: BackgroundSelectorModalProps) => {
  return (
    <Modal show={open} onClose={onClose} size="lg">
      {open && <BackgroundSelectorModalContent onSelect={onSelect} />}
    </Modal>
  );
};

interface ClassPillProps {
  className: string;
  onRemove: (className: string) => void;
}

const ClassPill = ({ className, onRemove }: ClassPillProps) => (
  <span className="badge bg-blue-100 text-blue-700 d-inline-flex align-items-center me-1 mb-1 px-2 py-1 rounded-pill">
    {className}
    <button
      type="button"
      className="btn-close btn-close-sm ms-1 bg-transparent border-0 p-0"
      onClick={() => onRemove(className)}
      style={{ fontSize: '0.6rem' }}
    >
      <i className="fa-regular fa-xmark" />
    </button>
  </span>
);

interface PageClassPickerProps {
  editor: CustomEditor;
  pageUuid?: string;
  docCss?: string;
  documentPermissions: string[];
}

const PageClassPicker = ({ editor, pageUuid, docCss, documentPermissions }: PageClassPickerProps) => {
  const { t } = useTranslation('doc-editor');

  const { user } = useUser();
  const lang = user?.userProperties?.cultureId ?? 'sv';
  const { classes: availableClassObjects, error: parseError } = useMemo(() => customClassExtractor(docCss || '', 'page'), [docCss]);
  const availableClasses = useMemo(() => availableClassObjects.map((c) => c.className), [availableClassObjects]);
  const classLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of availableClassObjects) {
      map[c.className] = lang === 'en' ? c.labelEn : c.labelSv;
    }
    return map;
  }, [availableClassObjects, lang]);

  const currentClasses = useMemo(() => {
    const [pageEntry] = Editor.nodes<PageElement>(editor, {
      at: [],
      match: (n) => Element.isElement(n) && n.type === 'page' && n.uuid === pageUuid,
    });
    if (!pageEntry) return [];
    const classStr = pageEntry[0].class || '';
    return classStr
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editor.children triggers recalc on content change
  }, [editor, pageUuid, editor.children]);

  const updateClasses = (newClasses: string[]) => {
    const classStr = newClasses.join(', ');
    const [pageEntry] = Editor.nodes<PageElement>(editor, {
      at: [],
      match: (n) => Element.isElement(n) && n.type === 'page' && n.uuid === pageUuid,
    });
    if (pageEntry) {
      Transforms.setNodes<PageElement>(editor, { class: classStr || undefined }, { at: pageEntry[1] });
    }
  };

  const handleAdd = (cls: string) => {
    if (!currentClasses.includes(cls)) {
      updateClasses([...currentClasses, cls]);
    }
  };

  const handleRemove = (cls: string) => {
    updateClasses(currentClasses.filter((c) => c !== cls));
  };

  const unselectedClasses = useMemo(() => availableClasses.filter((cls) => !currentClasses.includes(cls)), [availableClasses, currentClasses]);

  if (!(documentPermissions.includes('CODER') || documentPermissions.includes('DESIGN'))) return null;
  if (availableClasses.length === 0 && !parseError) return null;

  return (
    <div className="mt-4">
      <div className="text-blue-700 fw-bolder mb-1">{t('sidebar.page-settings.page-css-classes')}</div>
      {parseError && (
        <div className="alert alert-warning py-1 px-2 fs-7 mb-2">
          <i className="fa-regular fa-triangle-exclamation me-1" />
          {parseError}
        </div>
      )}
      {availableClasses.length > 0 && (
        <div className="rw-dropdown-wrapper">
          <DropdownList
            data={unselectedClasses.map((cls): ClassOption => ({ id: cls, value: cls, label: classLabelMap[cls] || cls }))}
            textField="label"
            placeholder={t('sidebar.page-settings.select-css-classes')}
            onSelect={(item: ClassOption) => handleAdd(item.value)}
            value={null}
            filter="contains"
            selectIcon={<i className="fa-regular fa-chevron-down" />}
            onChange={() => {}}
          />
        </div>
      )}
      {currentClasses.length > 0 && (
        <div className="d-flex flex-wrap mt-2">
          {currentClasses.map((cls) => (
            <ClassPill key={cls} className={cls} onRemove={handleRemove} />
          ))}
        </div>
      )}
    </div>
  );
};

interface PageVariablesProps {
  selectedPage: number;
  setSelectedPage: (value: number | null) => void;
}

export const PageVariables = ({ selectedPage, setSelectedPage }: PageVariablesProps) => {
  const { t } = useTranslation(['doc-editor', 'project-details', 'templates', 'editor']);

  const editor = useSlate();
  const { data: pDocument } = useEditorDocument();
  const { activeSection, isDirty } = useEditorCoarse();
  const { data: documentTemplates, isLoading: isDocumentTemplatesLoading } = useDocumentTemplates(pDocument.superId);
  const documentCss = useEditorCss().shadowedCss;
  const section = useEditorLiveSection();
  const styleId = pDocument.content.design.find((style) => style.pageId === activeSection.uuid)?.styleId;
  const content = useStyle({ organization: pDocument.organization, documentRevisionId: pDocument.revisionId, styleId }).data as DesignStyle | undefined;
  const [localStyle, setLocalStyle] = useState<PageStyleMap | null>(null);
  const { mutate: saveStyle } = useSaveStyle({
    organization: pDocument.organization,
    documentSuperId: pDocument.superId,
    pageId: activeSection.uuid,
    templateSuperId: activeSection.templateSuperId,
  });

  const { mutateAsync: movePage } = useMovePageToSection({
    onPageRemoved: (fromPage, document) => {
      const { superId, pageIndex } = fromPage as FormattedPage;
      const sourceRevisionId = (document as EditorDocument).content.sections
        .flatMap((section: SectionEntry) => [section, ...(section.targetLanguages ?? [])])
        .find((section) => section.superId === superId)?.revisionId;
      if (sourceRevisionId) editor.adoptSectionRevision?.(sourceRevisionId);
      HistoryEditor.withoutSaving(editor, () => {
        Transforms.removeNodes(editor, { at: [pageIndex], match: (node) => Element.isElement(node) && node.type === 'page' });
      });
      editor.history = { undos: [], redos: [] };
    },
  });
  const { mutateAsync: clonePage } = useClonePage({ documentSuperId: pDocument.superId });
  const { documentPermissions } = usePermissions(pDocument.superId);
  const setEditorDocument = useSetEditorDocument();
  const style: PageStyleMap | undefined =
    localStyle ?? (content && 'content' in content ? (content.content as PageStyleMap) : (content as PageStyleMap | undefined));

  const template = documentTemplates?.find((tpl) => tpl.superId === activeSection.templateSuperId);
  const templateStructure = template?.structure;
  const templatePage = (Array.isArray(templateStructure) ? templateStructure[0] : templateStructure) as TemplatePageStructure | undefined;
  const templateAreas = templatePage?.areas;

  const onMovePage = async (data: MovePayload) => {
    if (isDirty) {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'warning',
        title: t('doc-editor:sidebar.page-settings.move-page.unsaved-title'),
        text: t('doc-editor:sidebar.page-settings.move-page.unsaved-message'),
      });
      return;
    }
    Transforms.select(editor, [0]);
    if (data.fromPage.sectionId === data.targetPage.sectionId && data.copyPage) {
      const pageContent = section.content[data.fromPage.pageIndex];
      const { clonedPage, pageId, idMappings } = cloneDeepPage(JSON.parse(JSON.stringify(pageContent)));
      clonePage({ pageId, fromPage: data.fromPage, targetPage: data.targetPage, idMappings, sourcePage: pageContent, clonedPage })
        .then((result) => {
          Transforms.insertNodes(editor, (result?.clonedPage ?? clonedPage) as Descendant, { at: [data.targetPage.pageIndex] });
        })
        .catch(() => {}); // useClonePage's onError already reports it
    } else {
      if (section.content.length === 1 && !data.copyPage) {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          title: t('doc-editor:sidebar.page-settings.move-page.error-title'),
          text: t('doc-editor:sidebar.page-settings.move-page.error-desc'),
        });
        return;
      }
      let lock;
      try {
        lock = (
          await api.GET('/locks/{documentSuperId}/{sectionId}', {
            params: { path: { documentSuperId: pDocument.superId, sectionId: data.targetPage.superId } },
          })
        ).data;
      } catch (e) {
        ServerErrorHandler(e);
        return;
      }
      if (lock?.isValidNow && !lock.isValidForUser) {
        const { firstName, lastName } = lock.lockOwner || {};
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'warning',
          title: t('doc-editor:sidebar.page-settings.move-page.locked-title'),
          text: firstName && lastName ? `${t('editor:readonly.lockedby')} ${firstName} ${lastName}` : t('editor:readonly.lockedby.unknown'),
        });
        return;
      }
      movePage({
        documentId: pDocument.superId,
        fromPage: data.fromPage,
        targetPage: data.targetPage,
        copyPage: data.copyPage,
      })
        .then((res) => setEditorDocument(res.data))
        .catch(async (e) => {
          if (!isRevisionMismatch(e)) return;
          try {
            const docs = (await api.GET('/documents/', { params: { query: { superId: pDocument.superId, current: 'true' } } })).data;
            if (docs?.length) setEditorDocument(docs[docs.length - 1]);
          } catch (err) {
            ServerErrorHandler(err);
            return;
          }
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'warning',
            title: t('doc-editor:sidebar.page-settings.move-page.conflict-title'),
            text: t('doc-editor:sidebar.page-settings.move-page.conflict-message'),
          });
        });
    }

    setSelectedPage(null);
  };

  async function handleChange(newProperty: StyleChange) {
    const newStyle: PageStyleMap = style ? Object.assign({}, style) : {};
    if (Object.values(newProperty)[0] === undefined) {
      delete newStyle[Object.keys(newProperty)[0]];
    } else {
      Object.assign(newStyle, newProperty);
    }

    setLocalStyle(newStyle);
    saveStyle(newStyle, {
      onSuccess: (doc) => {
        setEditorDocument(doc);
      },
    });
  }

  if ((style == null && styleId) || isDocumentTemplatesLoading) {
    return <Spinner />;
  }

  return (
    <div className="preview--container">
      <div className="d-flex px-3 pt-4 justify-content-between align-items-center pb-3 w-100 bg-white sticky-top">
        <div className="d-flex align-items-center justify-content-between">
          <button onClick={() => setSelectedPage(null)} className="btn-clear bg-transparent me-3" type="button">
            <i className="fa-regular fa-arrow-left" />
          </button>
          <div className="fw-bold">{t('doc-editor:sidebar.page-settings')}</div>
        </div>
        <div className="text-gray-800 fs-7">
          {t('doc-editor:sidebar.page-prefix')}
          {selectedPage}
        </div>
      </div>
      <div className="px-3 w-100">
        {activeSection.templateSuperId ? (
          <p>{`${t('templates:title')} - ${documentTemplates?.find((tpl) => tpl.superId === activeSection?.templateSuperId)?.name}`}</p>
        ) : (
          t('project-details:project-css.no-template')
        )}
        <HeaderFooterToggle
          headerHidden={'xrpShowHeader' in (style || {}) ? style?.xrpShowHeader === 'none' : undefined}
          footerHidden={'xrpShowFooter' in (style || {}) ? style?.xrpShowFooter === 'none' : undefined}
          templateHeaderHidden={templateAreas?.header?.hidden}
          templateFooterHidden={templateAreas?.footer?.hidden}
          onChange={handleChange}
        />
        <BackgroundPicker name="xrpBackgroundImage" onChange={handleChange} defaultValue={style?.['xrpBackgroundImage'] ?? null} />
        <PageClassPicker editor={editor} pageUuid={activeSection.uuid} docCss={documentCss} documentPermissions={documentPermissions} />
        <SettingsSlider
          name="xrpLineHeightScalingFactor"
          onChange={handleChange}
          defaultValue={style?.['xrpLineHeightScalingFactor'] ?? 0}
          step={0.0005}
          range={[-1.0, -0.5, 0, 0.5, 1]}
        />
        <SettingsSlider
          name="xrpFontSizeScalingFactor"
          onChange={handleChange}
          defaultValue={style?.['xrpFontSizeScalingFactor'] ?? 0}
          step={0.0005}
          range={[-1.0, -0.5, 0, 0.5, 1]}
        />
        <SettingsSlider
          name="xrpLetterSpacingScalingFactor"
          onChange={handleChange}
          defaultValue={style?.['xrpLetterSpacingScalingFactor'] ?? 0}
          step={0.0005}
          range={[-1.0, -0.5, 0, 0.5, 1]}
        />
        <MovePage
          selectedPage={selectedPage}
          documentRevisionId={pDocument.revisionId}
          sections={pDocument.content.sections ?? []}
          activeSectionId={section.revisionId}
          onMovePage={onMovePage}
        />
      </div>
    </div>
  );
};
